import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const LOGO_STYLE = {
  "Apple":   "font-display text-2xl font-medium text-zinc-200",
  "SAMSUNG": "font-bold tracking-[0.22em] text-xl text-zinc-200",
  "Google":  "font-medium text-2xl text-zinc-200 tracking-tight",
  "Xiaomi":  "font-display text-2xl font-medium text-[#FF6900]",
  "HUAWEI":  "font-bold text-xl tracking-tight text-zinc-200",
  "HONOR":   "font-bold text-xl tracking-[0.18em] text-zinc-200",
};

export default function PartnersSection() {
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data)); }, []);

  // Only smartphone brands in the marquee (exclude any A1/MTel/Wertgarantie if backend returns them)
  const brands = partners.filter((p) => LOGO_STYLE[p.logo_text]);
  const list = brands.length ? [...brands, ...brands] : [];

  return (
    <section className="py-16 md:py-24 bg-[#0A0A0B] border-y border-white/5" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Offizielle Partnerbetriebe</div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mt-3">
            Vertrauen Sie auf unsere Hauptpartner
          </h2>
        </div>

        {/* DUAL HERO: A1 + MTel side by side */}
        <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-5xl mx-auto mb-10 md:mb-14">

          {/* A1 Partner Card */}
          <div className="relative rounded-[24px] bg-gradient-to-br from-[#1a0608] via-[#101013] to-[#0A0A0B] border border-[#DC1928]/30 overflow-hidden group" data-testid="partner-hero-a1">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#FF1A2B]/25 blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-[#DC1928]/15 blur-3xl"></div>

            <div className="relative p-7 md:p-9 flex flex-col items-center text-center min-h-[260px] justify-between gap-5">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#FF1A2B]/30 blur-2xl rounded-full group-hover:bg-[#FF1A2B]/45 transition-colors"></div>
                <div className="relative bg-white rounded-xl px-5 py-4 border border-white/15 shadow-xl">
                  <img
                    src="/images/store/a1-exclusive-partner.png"
                    alt="A1 Exclusive Partner"
                    className="h-14 md:h-16 w-auto object-contain"
                    data-testid="a1-logo-image"
                  />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF2D40] font-bold">Offizieller Partner</div>
                <div className="font-display text-xl md:text-2xl font-semibold text-white mt-2 leading-tight">
                  Autorisierter A1 Shop
                </div>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                  Tarife · Verträge · Servicegarantie direkt bei uns im Haus.
                </p>
              </div>
            </div>
          </div>

          {/* MTel Partner Card */}
          <div className="relative rounded-[24px] bg-gradient-to-br from-[#0F1320] via-[#101013] to-[#0A0A0B] border border-[#3D5BFF]/25 overflow-hidden group" data-testid="partner-hero-mtel">
            <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-[#3D5BFF]/25 blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-[#DC1928]/12 blur-3xl"></div>

            <div className="relative p-7 md:p-9 flex flex-col items-center text-center min-h-[260px] justify-between gap-5">
              <div className="relative">
                <div className="absolute -inset-5 bg-white/12 blur-2xl rounded-full group-hover:bg-white/18 transition-colors"></div>
                <div className="relative bg-white rounded-xl px-6 py-4 border border-white/15 shadow-xl">
                  <img
                    src="/images/store/mtel-logo-transparent.png"
                    alt="MTel Austria"
                    className="h-12 md:h-16 w-auto object-contain"
                    data-testid="mtel-logo-image"
                  />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#FF7B8E] font-bold">Vertriebspartner</div>
                <div className="font-display text-xl md:text-2xl font-semibold text-white mt-2 leading-tight">
                  MTel Austria
                </div>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                  Weltweit verbunden — Roame in 40+ Ländern ohne Zusatzkosten. Tarif-Beratung vor Ort.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Wertgarantie trust bar */}
        <div className="max-w-5xl mx-auto mb-12 md:mb-16" data-testid="wertgarantie-bar">
          <div className="relative rounded-2xl bg-gradient-to-r from-[#0F0F12] via-[#101216] to-[#0F0F12] border border-white/8 px-6 py-5 md:px-8 md:py-6 flex flex-col md:flex-row items-center md:items-center gap-5 md:gap-8 overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-sky-500/10 blur-3xl"></div>

            <div className="relative shrink-0 bg-white rounded-xl px-4 py-3 border border-white/15 shadow-lg">
              <img
                src="/images/store/wertgarantie-logo.png"
                alt="Wertgarantie — Einfach. Gut. Geschützt."
                className="h-10 md:h-12 w-auto object-contain"
                data-testid="wertgarantie-logo"
              />
            </div>
            <div className="relative hidden md:block w-px h-12 bg-white/10"></div>
            <div className="relative text-center md:text-left flex-1">
              <div className="text-[10px] uppercase tracking-[0.24em] text-sky-400 font-bold">Versicherungspartner</div>
              <div className="font-display text-base md:text-lg font-semibold text-white mt-1">
                Geräteschutz mit Wertgarantie — einfach. gut. geschützt.
              </div>
              <p className="text-xs md:text-sm text-zinc-500 mt-1">
                Display- und Komplettschutz für dein Smartphone — direkt bei Vertragsabschluss buchbar.
              </p>
            </div>
          </div>
        </div>

        {/* Brand carousel below */}
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Wir reparieren &amp; verkaufen</div>
        </div>
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#0A0A0B] to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#0A0A0B] to-transparent z-10 pointer-events-none"></div>
          <div className="flex items-center gap-20 marquee-track" style={{ width: "max-content" }}>
            {list.map((p, i) => (
              <div
                key={`${p.name}-${i}`}
                className={`shrink-0 flex items-center justify-center min-w-32 h-12 opacity-60 hover:opacity-100 transition-opacity ${LOGO_STYLE[p.logo_text] || "font-bold text-xl text-zinc-200"}`}
                data-testid={`partner-${p.logo_text.toLowerCase()}`}
              >
                {p.logo_text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
